<template>
  <SFModal
    :visible="isOpen"
    class="relative mb-0 max-w-full md:mb-auto"
    :transition-component="SFFadeInFromBottomTransition"
    @close="toggle"
  >
    <div class="max-w-full text-xl font-medium text-gray-500">
      {{ $t('shop_selector.flyout_title') }}
    </div>

    <div class="mx-12 mb-5 mt-4 flex flex-col justify-center text-gray-600">
      <div class="-mx-20 border-b border-gray-200"></div>
      <span class="my-4">{{ $t('shop_selector.message') }}</span>
      <SFAutoComplete
        id="countrySelect"
        v-model="shopCountry"
        :data="sortedCountries"
        :label="$t('shop_selector.country')"
        label-key="name"
        prepend-icon
      />
      <SFAutoComplete
        id="languageSelect"
        v-model="shopLanguage"
        :data="shopCountry?.languages"
        class="mt-4"
        :label="$t('shop_selector.language')"
        :disabled="disableLangSelector"
        label-key="label"
        prepend-icon
      />
      <div class="my-2 flex items-center gap-2">
        <IconSnipesDeliverytruck class="size-6" />
        <span class="text-lg">{{ $t('shop_selector.delivery') }}</span>
      </div>
      <SFButton size="lg" class="text-xl !font-medium" @click="goToStore"
        >{{ $t('shop_selector.go_to_shop') }}
      </SFButton>
    </div>
  </SFModal>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useCurrentShop } from '@scayle/storefront-nuxt/composables'
import {
  SFButton,
  SFFadeInFromBottomTransition,
  SFModal,
} from '#storefront-ui/components'
import SFAutoComplete from '~/components/form/SFAutoComplete.vue'
import type { Country, Language } from '~/constants/countries'
import { countries } from '~/constants/countries'
import { useSlideIn } from '#storefront-ui'
import { navigateTo } from '#app/composables/router'
import { useTrackingEvents } from '~/composables'

const { toggle, isOpen } = useSlideIn('ShopSwitcherSlideIn')
const { trackSwitchShop } = useTrackingEvents()

const shopCountry = ref<Country>()
const shopLanguage = ref<Language>()

const currentShop = useCurrentShop()

// Sort countries alphabetically by name
const sortedCountries = computed(() => {
  return [...countries].sort((a, b) => {
    return a.name.localeCompare(b.name, 'en', {
      numeric: true,
      sensitivity: 'base',
    })
  })
})

const disableLangSelector = computed(
  () =>
    !shopCountry.value?.languages?.length ||
    shopCountry.value?.languages?.length === 1,
)
watch(
  () => shopCountry.value,
  (value: Country | undefined) => {
    if (value) {
      shopLanguage.value = value.languages[0]
    }
  },
)

const goToStore = async () => {
  trackSwitchShop({
    action: `destination: ${shopCountry.value?.languageCode?.toLocaleLowerCase()}`,
    label: `origin: ${currentShop?.value?.path}`,
  })

  await navigateTo(shopLanguage.value?.url, { external: true })
}

onMounted(() => {
  shopCountry.value = sortedCountries.value[0]
  shopLanguage.value = shopCountry.value?.languages[0]
})
</script>
